// src/App.tsx
import { useState, useRef, useEffect, useCallback } from "react";
import "./App.css";
import type {
  AnalyzeEtfResponse,
  AnalyzePairResponse,
  AnalyzeScreenerResponse,
  Form4ScreenerResponse,
  ScreenerStrategy,
  ChartInterval,
} from "./api/myTypes";
import {
  analyzeEtf,
  analyzePair,
  analyzeScreener,
  getScreenerStrategies,
  getForm4Insiders,
  shutdownApp,
  getPendingActions,
  getDataProvider,
  type AnalysisMode,
} from "./api/etfApi";

import EtfCard from "./components/EtfCard";
import AnalysisResult from "./components/AnalysisResult";
import PairCard from "./components/PairCard";
import PairAnalysisResult from "./components/PairAnalysisResult";
import ScreenerCard from "./components/ScreenerCard";
import ScreenerResult from "./components/ScreenerResult";
import Form4ScreenerResult from "./components/Form4ScreenerResult";
import PerformanceDashboard, { PerformanceDashboardDetails } from "./components/PerformanceDashboard";
import PositionTracker from "./components/PositionTracker";
import LoadingSpinner from "./components/LoadingSpinner";
import AgentProgress from "./components/AgentProgress";
import LoginForm from "./components/LoginForm";
import AutoCycle from "./components/AutoCycle";
import SOXLPullback from "./components/SOXLPullback";

type TabKey = "single" | "auto_cycle" | "scalper" | "pair" | "screener" | "performance" | "positions";

const TICKER_SUGGESTIONS = [
  { ticker: "SOXL", name: "Direxion Daily Semiconductor Bull 3X" },
  { ticker: "SOXS", name: "Direxion Daily Semiconductor Bear 3X" },
  { ticker: "QQQ", name: "Invesco QQQ Trust" },
  { ticker: "XLK", name: "Technology Select Sector SPDR" },
];

function filterSuggestions(input: string) {
  if (!input) return TICKER_SUGGESTIONS;
  return TICKER_SUGGESTIONS.filter(
    (s) =>
      s.ticker.toLowerCase().includes(input.toLowerCase()) ||
      s.name.toLowerCase().includes(input.toLowerCase())
  );
}

function App() {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    // Check if user is already logged in (has token in localStorage)
    const token = localStorage.getItem("auth_token");
    return !!token;
  });
  const [username, setUsername] = useState<string>(() => {
    return localStorage.getItem("username") || "";
  });

  const [activeTab, setActiveTab] = useState<TabKey>("single");
  const [pendingActionsCount, setPendingActionsCount] = useState(0);
  const [soxlBadgeState, setSoxlBadgeState] = useState(false);
  const [scalperBadgeState, setScalperBadgeState] = useState(false);
  const [scalperEnabled, setScalperEnabled] = useState(true);

  // Check for pending actions periodically
  useEffect(() => {
    const checkPending = async () => {
      try {
        const data = await getPendingActions();
        setPendingActionsCount(data.count);
      } catch (err) {
        // Silently fail - not critical
        console.error("Error checking pending actions:", err);
      }
    };

    // Check immediately
    checkPending();

    // Check every 15 minutes (900000 ms)
    const interval = setInterval(checkPending, 900000);

    return () => clearInterval(interval);
  }, []);

  // Load data provider configuration
  useEffect(() => {
    const loadDataProvider = async () => {
      try {
        const config = await getDataProvider();
        setDataProvider(config.provider);
        setPolygonAvailable(config.polygon_configured);
      } catch (err) {
        console.error("Error loading data provider config:", err);
        setDataProvider("auto"); // Default fallback
      }
    };
    loadDataProvider();
  }, []);

  // Check for pending actions when positions tab is activated
  useEffect(() => {
    if (activeTab === "positions") {
      const checkPending = async () => {
        try {
          const data = await getPendingActions();
          setPendingActionsCount(data.count);
        } catch (err) {
          // Silently fail - not critical
          console.error("Error checking pending actions:", err);
        }
      };
      checkPending();
    }
  }, [activeTab]);

  // Check Auto-Cycle badge state periodically (only when NOT on Auto-Cycle tab)
  // The Auto-Cycle tab component handles its own polling, so we don't need to poll when on that tab
  useEffect(() => {
    // Skip polling if we're already on the Auto-Cycle or Pullback tab (components handle their own polling)
    if (activeTab === "auto_cycle" || activeTab === "scalper") {
      return;
    }

    const checkAutoCycleBadge = async () => {
      try {
        const { getAutoCycleStatus } = await import("./api/autoCycleApi");
        const status = await getAutoCycleStatus();
        setSoxlBadgeState(status.badge_state);
      } catch (err) {
        // Silently fail - not critical
        console.debug("Error checking Auto-Cycle badge state:", err);
      }
    };

    // Check immediately
    checkAutoCycleBadge();

    // Check every 30 seconds
    const interval = setInterval(checkAutoCycleBadge, 30000);
    return () => clearInterval(interval);
  }, [activeTab]); // Re-run when tab changes

  // Check SOXL Pullback badge state (Entry valid) when NOT on Pullback tab
  useEffect(() => {
    if (activeTab === "scalper") return;

    const checkPullbackBadge = async () => {
      try {
        const { getSoxlPullbackSignal } = await import("./api/soxlPullbackApi");
        const data = await getSoxlPullbackSignal({ symbol: "SOXL" });
        setScalperBadgeState(data.next_action === "Entry valid");
      } catch (err) {
        console.debug("Error checking SOXL Pullback badge:", err);
      }
    };

    checkPullbackBadge();
    const interval = setInterval(checkPullbackBadge, 30000);
    return () => clearInterval(interval);
  }, [activeTab]);

  // NEW — Analysis model (Classic / Debate)
  const [analysisModelSingle, setAnalysisModelSingle] = useState("classic");
  const [analysisModelPair, setAnalysisModelPair] = useState("classic");

  // Already in 2.1.6
  const [recommendationLevel, setRecommendationLevel] = useState("Moderate");
  
  // Language selector (shared across all tabs)
  const [language, setLanguage] = useState("English");
  
  // Data provider configuration
  const [dataProvider, setDataProvider] = useState<string>("auto");
  const [polygonAvailable, setPolygonAvailable] = useState(false);

  // Single ETF states
  const [ticker, setTicker] = useState("SOXL");
  const [singleMode, setSingleMode] = useState<AnalysisMode>("standard");
  const [singleHorizon, setSingleHorizon] = useState(20);
  const [singleHorizonInput, setSingleHorizonInput] = useState<string>("20"); // Local input state for editing
  const [singleData, setSingleData] = useState<AnalyzeEtfResponse | null>(null);
  
  // Sync input value when singleHorizon changes externally
  useEffect(() => {
    setSingleHorizonInput(singleHorizon.toString());
  }, [singleHorizon]);
  
  // Auto-set ticker to SOXL when SOXL Tactical Trend is selected
  useEffect(() => {
    if (analysisModelSingle === "soxl_tactical_10m" && ticker !== "SOXL") {
      setTicker("SOXL");
    }
  }, [analysisModelSingle, ticker]);
  
  const [singleLoading, setSingleLoading] = useState(false);
  const [singleError, setSingleError] = useState<string | null>(null);
  
  // Position tracking fields (for SOXL tactical strategy)
  const [positionEntryPrice, setPositionEntryPrice] = useState<string>("");
  const [positionShares, setPositionShares] = useState<string>("");

  // Pair analysis states
  const [primaryTicker, setPrimaryTicker] = useState("SOXL");
  const [secondaryTicker, setSecondaryTicker] = useState("SOXS");
  const [pairMode, setPairMode] = useState<AnalysisMode>("standard");
  const [pairHorizon, setPairHorizon] = useState(20);
  const [pairData, setPairData] = useState<AnalyzePairResponse | null>(null);
  const [pairLoading, setPairLoading] = useState(false);
  const [pairError, setPairError] = useState<string | null>(null);

  // Screener states
  const [screenerTicker, setScreenerTicker] = useState("SOXL");
  const [screenerStrategy, setScreenerStrategy] = useState("momentum_breakout");
  const [userPrompt, setUserPrompt] = useState("");
  const [showUserPrompt, setShowUserPrompt] = useState(false);
  const [chartInterval, setChartInterval] = useState<ChartInterval>("1D");
  const [screenerStrategies, setScreenerStrategies] = useState<ScreenerStrategy[]>([]);
  const [screenerStrategiesLoading, setScreenerStrategiesLoading] = useState(true);
  const [screenerData, setScreenerData] = useState<AnalyzeScreenerResponse | null>(null);
  const [screenerLoading, setScreenerLoading] = useState(false);
  const [screenerError, setScreenerError] = useState<string | null>(null);
  const [form4Data, setForm4Data] = useState<Form4ScreenerResponse | null>(null);
  const [form4Loading, setForm4Loading] = useState(false);
  const [form4Error, setForm4Error] = useState<string | null>(null);

  // Print refs
  const printRef = useRef<HTMLDivElement>(null);
  const pairPrintRef = useRef<HTMLDivElement>(null);
  const screenerPrintRef = useRef<HTMLDivElement>(null);

  // Ticker focus/hover states
  const [tickerFocus, setTickerFocus] = useState(false);
  const [tickerHover, setTickerHover] = useState(false);
  const [primaryFocus, setPrimaryFocus] = useState(false);
  const [primaryHover, setPrimaryHover] = useState(false);
  const [secondaryFocus, setSecondaryFocus] = useState(false);
  const [secondaryHover, setSecondaryHover] = useState(false);
  const [screenerTickerFocus, setScreenerTickerFocus] = useState(false);
  const [screenerTickerHover, setScreenerTickerHover] = useState(false);

  // Load screener strategies with retries (backend may not be ready right after restart)
  const loadScreenerStrategies = useCallback(async () => {
    setScreenerStrategiesLoading(true);
    setScreenerError(null);
    const maxAttempts = 4;
    const delayMs = 2000;
    let lastError: Error | null = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const strategies = await getScreenerStrategies();
        setScreenerStrategies(strategies);
        if (strategies.length > 0) {
          setScreenerStrategy((prev) => prev || strategies[0].id);
        }
        setScreenerStrategiesLoading(false);
        return;
      } catch (e: unknown) {
        lastError = e instanceof Error ? e : new Error(String(e));
        if (attempt < maxAttempts) {
          await new Promise((r) => setTimeout(r, delayMs));
        }
      }
    }
    setScreenerStrategiesLoading(false);
    setScreenerError(
      lastError ? `Screener strategies: ${lastError.message}. Ensure backend is running and try Retry or refresh.` : "Failed to load screener strategies."
    );
    setScreenerStrategies([]);
  }, []);

  useEffect(() => {
    loadScreenerStrategies();
  }, [loadScreenerStrategies]);

  // Update showUserPrompt and chart interval when screener strategy changes
  useEffect(() => {
    setShowUserPrompt(screenerStrategy === "user_specified");
    
    // Auto-select required chart interval for strategies that need it
    const strategy = screenerStrategies.find(s => s.id === screenerStrategy);
    if (strategy?.required_chart_interval) {
      setChartInterval(strategy.required_chart_interval as ChartInterval);
    }
  }, [screenerStrategy, screenerStrategies]);
  
  // Get current strategy info for description
  const currentStrategy = screenerStrategies.find(s => s.id === screenerStrategy);
  
  // Function to get analysis model description for Single ETF
  const getSingleAnalysisModelDescription = () => {
    const modelDescriptions: Record<string, { title: string; content: React.ReactNode }> = {
      "classic": {
        title: "Classic Analysis Model",
        content: (
          <>
            <p style={{ margin: "0.5rem 0", color: "#555" }}>
              The Classic model uses a multi-agent approach with specialized analysts working in sequence:
            </p>
            <ul style={{ margin: "0.5rem 0", paddingLeft: "1.5rem", color: "#555" }}>
              <li><strong>Technical Analyst:</strong> Analyzes chart patterns, trends, support/resistance, and momentum signals</li>
              <li><strong>Relative Strength Scout:</strong> Compares ETF performance against benchmarks (SPY, QQQ, sector ETFs)</li>
              <li><strong>Macro Context Expert:</strong> Evaluates macro factors, economic events, and sector risks</li>
              <li><strong>Risk Manager:</strong> Synthesizes all inputs into risk assessment with stop zones and sizing guidance</li>
              <li><strong>Strategy Synthesizer:</strong> Creates final actionable recommendation with entry/exit zones, position sizing, and risk parameters</li>
              <li><strong>Verification Agent:</strong> Validates the recommendation for completeness and safety</li>
            </ul>
            <p style={{ margin: "0.5rem 0 0 0", color: "#666", fontSize: "0.85rem" }}>
              This model provides comprehensive analysis through sequential agent collaboration, ensuring thorough evaluation before generating recommendations.
            </p>
          </>
        )
      },
      "multi_turn_debate": {
        title: "Multi-Turn Debate Analysis Model",
        content: (
          <>
            <p style={{ margin: "0.5rem 0", color: "#555" }}>
              The Multi-Turn Debate model uses a competitive debate system where multiple agents propose and critique trading strategies:
            </p>
            <ul style={{ margin: "0.5rem 0", paddingLeft: "1.5rem", color: "#555" }}>
              <li><strong>Debate Proposal Agents:</strong> Multiple agents independently propose complete trading theses and plans</li>
              <li><strong>Debate Critic Agents:</strong> Evaluate each proposal for logical consistency, completeness, risk, and signal justification</li>
              <li><strong>Debate Judge:</strong> Selects the winning proposal based on balanced risk-reward, coherence, and actionability</li>
            </ul>
            <p style={{ margin: "0.5rem 0 0 0", color: "#666", fontSize: "0.85rem" }}>
              This model encourages diverse perspectives and competitive evaluation, resulting in well-vetted recommendations through agent debate.
            </p>
          </>
        )
      },
      "chart_pattern_analysis": {
        title: "Chart Pattern Analysis Model",
        content: (
          <>
            <p style={{ margin: "0.5rem 0", color: "#555" }}>
              The Chart Pattern Analysis model executes 10 complementary chart pattern strategies in parallel:
            </p>
            <ul style={{ margin: "0.5rem 0", paddingLeft: "1.5rem", color: "#555" }}>
              <li><strong>Support & Resistance Levels:</strong> Identifies key price levels from history and volume clusters</li>
              <li><strong>Trend Direction & Strength:</strong> Analyzes trend direction and strength using moving averages</li>
              <li><strong>Breakout Patterns:</strong> Identifies consolidation patterns and breakout signals</li>
              <li><strong>Candlestick Reversal Patterns:</strong> Detects reversal and continuation candlestick patterns</li>
              <li><strong>Volume Profile & Divergence:</strong> Analyzes volume trends and volume-price divergence</li>
              <li><strong>Momentum & Oscillator Signals:</strong> Evaluates RSI, MACD, and momentum indicators</li>
              <li><strong>Classic Chart Patterns:</strong> Identifies head and shoulders, double tops/bottoms, triangles, etc.</li>
              <li><strong>Fibonacci Retracement & Extension:</strong> Calculates Fibonacci levels and targets</li>
              <li><strong>Volatility & Range Analysis:</strong> Assesses volatility expansion/contraction and Bollinger Bands</li>
              <li><strong>Multi-Timeframe Alignment:</strong> Analyzes alignment across multiple timeframes</li>
            </ul>
            <p style={{ margin: "0.5rem 0 0 0", color: "#666", fontSize: "0.85rem" }}>
              All 10 strategies are executed, then consolidated and verified to produce a final recommendation with consensus from multiple pattern analysis approaches.
            </p>
          </>
        )
      },
      "triple_leveraged_bb_lrc_scale": {
        title: "Triple Leveraged BB LRC Scale",
        content: (
          <>
            <p style={{ margin: "0.5rem 0", color: "#555" }}>
              <strong>Description:</strong> A deterministic scaling strategy for triple-leveraged ETFs using Bollinger Bands, RSI, and Linear Regression Channel.
            </p>
            <ul style={{ margin: "0.5rem 0", paddingLeft: "1.5rem", color: "#555" }}>
              <li><strong>Entry:</strong> First buy when price ≤ 4h BB lower AND RSI4h ≤ 30</li>
              <li><strong>Scaling:</strong> Add tranches when price drops 10%+ from last buy, confirmed by 45m BB lower</li>
              <li><strong>Exit:</strong> Sell 100% when LRC position ≥ 95%</li>
              <li><strong>Limits:</strong> Max 2 buys/day, max 5 tranches total</li>
              <li><strong>Data Source:</strong> Requires Polygon.io for 45m and 4h bars</li>
            </ul>
            <p style={{ margin: "0.5rem 0 0 0", color: "#666", fontSize: "0.85rem" }}>
              This strategy uses deterministic rule-based entry/exit logic with scaling tranches to manage risk in volatile triple-leveraged ETFs.
            </p>
          </>
        )
      },
      "soxl_tactical_10m": {
        title: "SOXL Tactical Trend Model",
        content: (
          <>
            <p style={{ margin: "0.5rem 0", color: "#555" }}>
              The SOXL Tactical Trend model is a specialized strategy designed for trading SOXL (Direxion Daily Semiconductor Bull 3X) using 10-minute chart intervals:
            </p>
            <ul style={{ margin: "0.5rem 0", paddingLeft: "1.5rem", color: "#555" }}>
              <li><strong>Tactical Entry Signals:</strong> Identifies precise entry points based on short-term momentum and trend alignment</li>
              <li><strong>10-Minute Timeframe Focus:</strong> Optimized for intraday trading with 10-minute candle analysis</li>
              <li><strong>Risk Management:</strong> Provides tight stop-loss levels and position sizing recommendations</li>
              <li><strong>Trend Confirmation:</strong> Uses multiple technical indicators to confirm trend direction before entry</li>
              <li><strong>Exit Strategy:</strong> Defines clear profit targets and exit conditions based on tactical analysis</li>
            </ul>
            <p style={{ margin: "0.5rem 0 0 0", color: "#666", fontSize: "0.85rem" }}>
              This model is specifically tuned for SOXL's high volatility and leverage characteristics, providing tactical trading recommendations optimized for short-term moves.
            </p>
            <p style={{ margin: "0.75rem 0 0 0", color: "#666", fontSize: "0.85rem", fontStyle: "italic" }}>
              Run once daily after the first hour of trading (≈10:30 AM ET). This strategy uses daily trend context with intraday confirmation and is designed for swing trades, not intraday scalping.
            </p>
          </>
        )
      }
    };
    
    return modelDescriptions[analysisModelSingle] || null;
  };
  
  // Function to get analysis model description for Pair & Rotation
  const getPairAnalysisModelDescription = () => {
    const modelDescriptions: Record<string, { title: string; content: React.ReactNode }> = {
      "classic": {
        title: "Classic Analysis Model",
        content: (
          <>
            <p style={{ margin: "0.5rem 0", color: "#555" }}>
              The Classic model for pair rotation uses a multi-agent approach:
            </p>
            <ul style={{ margin: "0.5rem 0", paddingLeft: "1.5rem", color: "#555" }}>
              <li><strong>Pair Rotation Agent:</strong> Analyzes the relationship between two ETFs, correlation, and rotation opportunities</li>
              <li><strong>Risk Manager:</strong> Evaluates risk for the pair rotation strategy</li>
              <li><strong>Strategy Synthesizer:</strong> Creates rotation recommendation with entry/exit zones for both tickers</li>
              <li><strong>Verification Agent:</strong> Validates the rotation recommendation for completeness and safety</li>
            </ul>
            <p style={{ margin: "0.5rem 0 0 0", color: "#666", fontSize: "0.85rem" }}>
              This model provides comprehensive pair analysis to identify optimal rotation opportunities between two related ETFs.
            </p>
          </>
        )
      },
      "multi_turn_debate": {
        title: "Multi-Turn Debate Analysis Model",
        content: (
          <>
            <p style={{ margin: "0.5rem 0", color: "#555" }}>
              The Multi-Turn Debate model for pair rotation uses a competitive debate system:
            </p>
            <ul style={{ margin: "0.5rem 0", paddingLeft: "1.5rem", color: "#555" }}>
              <li><strong>Debate Proposal Agents:</strong> Multiple agents independently propose pair rotation strategies</li>
              <li><strong>Debate Critic Agents:</strong> Evaluate each proposal for logical consistency, risk, and rotation timing</li>
              <li><strong>Debate Judge:</strong> Selects the winning rotation proposal based on balanced risk-reward and actionability</li>
            </ul>
            <p style={{ margin: "0.5rem 0 0 0", color: "#666", fontSize: "0.85rem" }}>
              This model encourages diverse perspectives on pair rotation, resulting in well-vetted recommendations through agent debate.
            </p>
          </>
        )
      }
    };
    
    return modelDescriptions[analysisModelPair] || null;
  };
  
  // Function to get strategy description content
  const getStrategyDescription = () => {
    if (!currentStrategy) return null;
    
    const strategyId = currentStrategy.id;
    const strategyName = currentStrategy.name;
    const description = currentStrategy.description || "";
    
    // Custom descriptions for each strategy
    const strategyDescriptions: Record<string, { title: string; content: React.ReactNode }> = {
      "momentum_breakout": {
        title: "Momentum Breakout Strategy",
        content: (
          <>
            <p style={{ margin: "0.5rem 0", color: "#555" }}>
              <strong>Description:</strong> {description}
            </p>
            <p style={{ margin: "0.5rem 0", color: "#555" }}>
              This strategy identifies ETFs breaking out of consolidation patterns with strong momentum. The analysis looks for:
            </p>
            <ul style={{ margin: "0.5rem 0", paddingLeft: "1.5rem", color: "#555" }}>
              <li>Price breaking above key resistance levels</li>
              <li>Increasing volume on the breakout</li>
              <li>Strong relative strength vs market</li>
              <li>Technical indicators confirming momentum (RSI, MACD, etc.)</li>
              <li>Potential entry/exit levels with risk/reward assessment</li>
            </ul>
          </>
        )
      },
      "oversold_bounce": {
        title: "Oversold Bounce Strategy",
        content: (
          <>
            <p style={{ margin: "0.5rem 0", color: "#555" }}>
              <strong>Description:</strong> {description}
            </p>
            <p style={{ margin: "0.5rem 0", color: "#555" }}>
              This strategy identifies ETFs that are oversold and may be due for a bounce. The analysis evaluates:
            </p>
            <ul style={{ margin: "0.5rem 0", paddingLeft: "1.5rem", color: "#555" }}>
              <li>RSI below 30 (oversold territory)</li>
              <li>Price near key support levels</li>
              <li>Divergence patterns indicating potential reversal</li>
              <li>Volume characteristics and historical bounce patterns</li>
              <li>Risk factors and bounce probability assessment</li>
            </ul>
          </>
        )
      },
      "trend_reversal": {
        title: "Trend Reversal Strategy",
        content: (
          <>
            <p style={{ margin: "0.5rem 0", color: "#555" }}>
              <strong>Description:</strong> {description}
            </p>
            <p style={{ margin: "0.5rem 0", color: "#555" }}>
              This strategy identifies potential trend reversal points. The analysis looks for:
            </p>
            <ul style={{ margin: "0.5rem 0", paddingLeft: "1.5rem", color: "#555" }}>
              <li>Trend exhaustion patterns</li>
              <li>Divergence in indicators</li>
              <li>Volume patterns indicating reversal</li>
              <li>Support/resistance interactions</li>
              <li>Reversal candlestick patterns</li>
              <li>Confirmation signals needed for entry</li>
            </ul>
          </>
        )
      },
      "first_candle_rule": {
        title: "First Candle Rule Strategy",
        content: (
          <>
            <p style={{ margin: "0.5rem 0", color: "#555" }}>
              <strong>Description:</strong> {description}
            </p>
            <p style={{ margin: "0.5rem 0", color: "#555" }}>
              This strategy analyzes the first 15-minute candle of the trading day to identify breakout levels and trading opportunities.
            </p>
            <ul style={{ margin: "0.5rem 0", paddingLeft: "1.5rem", color: "#555" }}>
              <li><strong>First Candle Identification:</strong> Extracts the HIGH and LOW prices from the first 15-minute candle after market open (9:30 AM ET)</li>
              <li><strong>Breakout Zones:</strong> Price breaking above the first candle HIGH = Potential LONG signal; Price breaking below the first candle LOW = Potential SHORT signal</li>
              <li><strong>Fair Value Gaps (FVG):</strong> Identifies price ranges where minimal trading occurred, indicating potential support/resistance</li>
              <li><strong>Required Interval:</strong> This strategy requires 15-minute chart intervals</li>
            </ul>
            <p style={{ margin: "0.5rem 0 0 0", color: "#666", fontSize: "0.85rem" }}>
              The chart interval will automatically be set to 15m when this strategy is selected.
            </p>
          </>
        )
      },
      "macd_money_map": {
        title: "MACD Money Map - Three Sequential Systems",
        content: (
          <>
            <p style={{ margin: "0.5rem 0", color: "#555" }}>
              <strong>Description:</strong> {description}
            </p>
            <ul style={{ margin: "0.5rem 0", paddingLeft: "1.5rem", color: "#555" }}>
              <li><strong>System 1 - Trend System:</strong> Identifies trend-following MACD continuation signals using zero line bias, crossovers, distance rule, and patience rule.</li>
              <li><strong>System 2 - Reversal System:</strong> Detects MACD divergence reversals with histogram pattern confirmation (flip, shrinking tower, zero bounce).</li>
              <li><strong>System 3 - Confirmation System:</strong> Filters signals using multi-timeframe agreement and price-action confluence to grade signal strength.</li>
            </ul>
            <p style={{ margin: "0.5rem 0 0 0", color: "#666", fontSize: "0.85rem" }}>
              The analysis will execute all three systems sequentially and provide final graded recommendations.
            </p>
          </>
        )
      },
      "user_specified": {
        title: "User Specified Strategy",
        content: (
          <>
            <p style={{ margin: "0.5rem 0", color: "#555" }}>
              <strong>Description:</strong> {description}
            </p>
            <p style={{ margin: "0.5rem 0", color: "#555" }}>
              This strategy allows you to provide a custom analysis prompt. Enter your specific analysis requirements in the text area below.
            </p>
            <p style={{ margin: "0.5rem 0 0 0", color: "#666", fontSize: "0.85rem" }}>
              The LLM will analyze the ETF based on your custom instructions and return structured results.
            </p>
          </>
        )
      }
    };
    
    return strategyDescriptions[strategyId] || {
      title: `${strategyName} Strategy`,
      content: (
        <p style={{ margin: "0.5rem 0", color: "#555" }}>
          <strong>Description:</strong> {description}
        </p>
      )
    };
  };

  // Force 15m chart interval when First Candle Rule is selected
  useEffect(() => {
    if (screenerStrategy === "first_candle_rule") {
      setChartInterval("15m");
    }
  }, [screenerStrategy]);

  const handlePrintAnalysis = () => {
    const printContents =
      activeTab === "single" ? printRef.current :
      activeTab === "pair" ? pairPrintRef.current :
      screenerPrintRef.current;

    if (!printContents) {
      window.print();
      return;
    }

    // Clone the content and remove LLM dialog wrapper
    const clone = printContents.cloneNode(true) as HTMLElement;
    const dialogWrapper = clone.querySelector('.llm-dialog-wrapper');
    if (dialogWrapper) {
      dialogWrapper.remove();
    }

    const printWindow = window.open("", "", "width=900,height=650");

    if (printWindow) {
      printWindow.document.write(`
      <html>
        <head>
          <title>ETF Analysis Printout</title>
          <style>
            body { font-family: sans-serif; margin: 32px; color: #000 !important; }
            h1,h2,h3,h4,h5,h6 { color: #000 !important; }
            .panel, .panel-inner, .card {
              border: none !important;
              box-shadow: none !important;
              background: transparent !important;
            }
            .scrollable-reasoning {
              background: transparent !important;
              border: none !important;
              max-height: none !important;
              overflow: visible !important;
              padding: 0 !important;
            }
            *::-webkit-scrollbar { display: none !important; }
          </style>
        </head>
        <body>${clone.innerHTML}</body>
      </html>
      `);

      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 200);
    }
  };

  const handlePrintDialog = () => {
    const printContents =
      activeTab === "single" ? printRef.current :
      activeTab === "pair" ? pairPrintRef.current :
      screenerPrintRef.current;

    if (!printContents) {
      return;
    }

    // Find the LLM dialog wrapper inside the print contents
    const dialogWrapper = printContents.querySelector('.llm-dialog-wrapper');
    if (!dialogWrapper) {
      return;
    }

    const printWindow = window.open("", "", "width=900,height=650");

    if (printWindow) {
      printWindow.document.write(`
      <html>
        <head>
          <title>Agent Dialog Printout</title>
          <style>
            body { font-family: sans-serif; margin: 32px; color: #000 !important; }
            h1,h2,h3,h4,h5,h6 { color: #000 !important; }
            .panel, .panel-inner, .card {
              border: none !important;
              box-shadow: none !important;
              background: transparent !important;
            }
            .scrollable-reasoning {
              background: transparent !important;
              border: none !important;
              max-height: none !important;
              overflow: visible !important;
              padding: 0 !important;
            }
            *::-webkit-scrollbar { display: none !important; }
          </style>
        </head>
        <body>${dialogWrapper.innerHTML}</body>
      </html>
      `);

      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 200);
    }
  };

  // ---- SINGLE ETF ANALYSIS ----
  const handleAnalyzeSingle = async () => {
    setSingleError(null);
    setSingleData(null);

    if (!ticker.trim()) {
      setSingleError("Please enter an ETF ticker.");
      return;
    }

    setSingleLoading(true);
    try {
      const res = await analyzeEtf({
        ticker: ticker.trim(),
        timeHorizonDays: singleHorizon,
        analysisMode: singleMode,
        analysisModel: analysisModelSingle, // NEW
        recommendationLevel,
        language,
        positionEntryPrice: positionEntryPrice ? parseFloat(positionEntryPrice) : undefined,
        positionShares: positionShares ? parseFloat(positionShares) : undefined,
      });
      setSingleData(res);
    } catch (e: any) {
      setSingleError(e?.message || "Analysis failed.");
    } finally {
      setSingleLoading(false);
    }
  };

  // ---- PAIR ANALYSIS ----
  const handleAnalyzePair = async () => {
    setPairError(null);
    setPairData(null);

    if (!primaryTicker.trim() || !secondaryTicker.trim()) {
      setPairError("Both primary and secondary tickers are required.");
      return;
    }

    setPairLoading(true);
    try {
      const res = await analyzePair({
        primaryTicker: primaryTicker.trim(),
        secondaryTicker: secondaryTicker.trim(),
        timeHorizonDays: pairHorizon,
        analysisMode: pairMode,
        analysisModel: analysisModelPair, // NEW
        recommendationLevel: recommendationLevel, // NEW
        language,
      });
      setPairData(res);
    } catch (e: any) {
      setPairError(e?.message || "Pair analysis failed.");
    } finally {
      setPairLoading(false);
    }
  };

  // ---- SCREENER ANALYSIS ----
  const handleAnalyzeScreener = async () => {
    setScreenerError(null);
    setScreenerData(null);
    setForm4Error(null);
    setForm4Data(null);

    if (screenerStrategy === "form4_insiders") {
      setForm4Loading(true);
      try {
        const res = await getForm4Insiders({
          days_recent: 2,
          days_aggregate: 7,
          max_tickers: 20,
          max_price: 20,
          only_open_market_buys: false,
          include_sells: true,
        });
        setForm4Data(res);
      } catch (e: any) {
        setForm4Error(e?.message || "Form 4 screener failed.");
      } finally {
        setForm4Loading(false);
      }
      return;
    }

    if (!screenerTicker.trim()) {
      setScreenerError("Please enter an ETF ticker.");
      return;
    }

    if (screenerStrategy === "user_specified" && !userPrompt.trim()) {
      setScreenerError("Please enter a custom prompt for user-specified screener.");
      return;
    }

    setScreenerLoading(true);
    try {
      const res = await analyzeScreener({
        ticker: screenerTicker.trim(),
        screener: screenerStrategy,
        userPrompt: screenerStrategy === "user_specified" ? userPrompt.trim() : undefined,
        chartInterval: chartInterval,
        recommendationLevel: recommendationLevel,
        language,
      });
      setScreenerData(res);
    } catch (e: any) {
      setScreenerError(e?.message || "Screener analysis failed.");
    } finally {
      setScreenerLoading(false);
    }
  };

  // -------------------------------
  // RENDER UI
  // -------------------------------
  const handleExit = async () => {
    if (window.confirm("Are you sure you want to exit the application?")) {
      await shutdownApp();
    }
  };

  // Handle successful login
  const handleLoginSuccess = (_token: string, username: string) => {
    setIsAuthenticated(true);
    setUsername(username);
  };

  // Handle logout
  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("username");
      setIsAuthenticated(false);
      setUsername("");
    }
  };

  // Check if running on local server (not droplet)
  const isLocalServer = (): boolean => {
    // First check the current window location
    const currentHost = window.location.hostname;
    if (currentHost === "localhost" || currentHost === "127.0.0.1" || currentHost === "0.0.0.0" || currentHost === "") {
      return true;
    }
    
    // Also check VITE_API_URL if set
    const envUrl = import.meta.env.VITE_API_URL?.toString()?.trim();
    if (envUrl) {
      const url = envUrl.startsWith("http://") || envUrl.startsWith("https://") 
        ? envUrl 
        : `http://${envUrl}`;
      try {
        const urlObj = new URL(url);
        const hostname = urlObj.hostname;
        return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "0.0.0.0";
      } catch {
        // If URL parsing fails, check if it contains localhost or 127.0.0.1
        return envUrl.includes("localhost") || envUrl.includes("127.0.0.1");
      }
    }
    
    // If no VITE_API_URL is set and we're in development mode, assume local
    if (!import.meta.env.PROD) {
      return true;
    }
    
    // Production mode on droplet (has a domain name, not localhost)
    return false;
  };

  const showExitButton = isLocalServer();

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return <LoginForm onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="app-root">
      <header className="app-header">
        <div>
          <h1>StockBot Pro</h1>
          <p className="app-subtitle">
            Multi-agent ETF analysis with technicals, risk, rotation, and macro context.
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {/* Data Provider Indicator */}
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "6px",
            padding: "4px 10px",
            borderRadius: "12px",
            backgroundColor: "rgba(15, 23, 42, 0.9)",
            border: "1px solid rgba(148, 163, 184, 0.3)",
            fontSize: "0.75rem",
            color: "#9ca3af"
          }}>
            <span style={{ color: "#6b7280" }}>Data:</span>
            <span style={{ 
              color: polygonAvailable && dataProvider === "polygon" ? "#22c55e" : 
                     dataProvider === "yahoo" ? "#3b82f6" : "#f59e0b",
              fontWeight: 600
            }}>
              {dataProvider === "auto" ? "Auto" : 
               dataProvider === "polygon" ? "Polygon" : 
               dataProvider === "yahoo" ? "Yahoo" : dataProvider}
            </span>
          </div>
          
          {/* Logged in user indicator */}
          {username && (
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: "6px",
              padding: "4px 10px",
              borderRadius: "12px",
              backgroundColor: "rgba(15, 23, 42, 0.9)",
              border: "1px solid rgba(148, 163, 184, 0.3)",
              fontSize: "0.75rem",
              color: "#9ca3af"
            }}>
              <span style={{ color: "#6b7280" }}>User:</span>
              <span style={{ 
                color: "#6366f1",
                fontWeight: 600
              }}>
                {username}
              </span>
            </div>
          )}
          
          <button
            className="primary-button"
            onClick={handleLogout}
            style={{
              padding: "6px 12px",
              fontSize: "0.875rem",
              backgroundColor: "#6366f1",
            }}
            title="Logout"
          >
            Logout
          </button>
          {showExitButton && (
            <button
              className="primary-button"
              onClick={handleExit}
              style={{
                padding: "6px 12px",
                fontSize: "0.875rem",
                backgroundColor: "#dc2626",
              }}
              title="Exit application"
            >
              Exit
            </button>
          )}
        </div>
      </header>

      <div className="tabs">
        <button
          className={`tab ${activeTab === "single" ? "active" : ""}`}
          onClick={() => setActiveTab("single")}
        >
          Single ETF
        </button>
        <button
          className={`tab ${activeTab === "auto_cycle" ? "active" : ""}`}
          onClick={() => setActiveTab("auto_cycle")}
          style={{ position: "relative" }}
        >
          Auto Cycle (SOXL / SOXS)
          {soxlBadgeState && (
            <span
              style={{
                position: "absolute",
                top: "-4px",
                right: "-4px",
                width: "12px",
                height: "12px",
                backgroundColor: "#ef4444",
                borderRadius: "50%",
                border: "2px solid #1a1d29",
              }}
              title="Action recommended (BUY or SELL)"
            />
          )}
        </button>
        <button
          className={`tab ${activeTab === "scalper" ? "active" : ""}`}
          onClick={() => setActiveTab("scalper")}
          style={{ position: "relative" }}
        >
          SOXL Pullback (Moderate)
          {scalperBadgeState && (
            <span
              style={{
                position: "absolute",
                top: "-4px",
                right: "-4px",
                width: "12px",
                height: "12px",
                backgroundColor: "#ef4444",
                borderRadius: "50%",
                border: "2px solid #1a1d29",
              }}
              title="Action recommended (BUY or SELL)"
            />
          )}
        </button>
        <button
          className={`tab ${activeTab === "pair" ? "active" : ""}`}
          onClick={() => setActiveTab("pair")}
        >
          Pair & Rotation
        </button>
        <button
          className={`tab ${activeTab === "screener" ? "active" : ""}`}
          onClick={() => setActiveTab("screener")}
        >
          Screener
        </button>
        <button
          className={`tab ${activeTab === "performance" ? "active" : ""}`}
          onClick={() => setActiveTab("performance")}
        >
          Performance
        </button>
        <button
          className={`tab ${activeTab === "positions" ? "active" : ""}`}
          onClick={() => setActiveTab("positions")}
          style={{ position: "relative" }}
        >
          Positions
          {pendingActionsCount > 0 && (
            <span
              style={{
                position: "absolute",
                top: "-4px",
                right: "-4px",
                width: "12px",
                height: "12px",
                backgroundColor: "#ef4444",
                borderRadius: "50%",
                border: "2px solid #1a1d29",
              }}
              title={`${pendingActionsCount} pending action${pendingActionsCount !== 1 ? 's' : ''}`}
            />
          )}
        </button>
      </div>

      {/* ======================== */}
      {/* SINGLE ETF PANEL */}
      {/* ======================== */}
      {activeTab === "single" ? (
        <main className="main-layout">
          <section className="panel panel-left">
            <div className="panel-inner">
              <h2 className="panel-title">Analyze a single ETF</h2>
              <p className="panel-description">
                Type a ticker (e.g., SOXL, QQQ) to run a multi-agent analysis.
              </p>

              {/* Ticker input */}
              <div className="field">
                <label className="field-label">
                  ETF ticker
                  {analysisModelSingle === "soxl_tactical_10m" && (
                    <span style={{ fontSize: "0.75rem", color: "#666", marginLeft: "0.5rem", fontStyle: "italic" }}>
                      (SOXL-specific strategy)
                    </span>
                  )}
                </label>
                <div className="ticker-search-wrapper" style={{ position: "relative" }}>
                  <input
                    className="text-input"
                    value={analysisModelSingle === "soxl_tactical_10m" ? "SOXL" : ticker}
                    onChange={(e) => {
                      if (analysisModelSingle !== "soxl_tactical_10m") {
                        setTicker(e.target.value.toUpperCase());
                      }
                    }}
                    disabled={analysisModelSingle === "soxl_tactical_10m"}
                    style={analysisModelSingle === "soxl_tactical_10m" ? { 
                      backgroundColor: "#2c2c2c", 
                      color: "#666", 
                      cursor: "not-allowed",
                      opacity: 0.6 
                    } : {}}
                    onFocus={() => {
                      if (analysisModelSingle !== "soxl_tactical_10m") {
                        setTickerFocus(true);
                      }
                    }}
                    onBlur={() => setTimeout(() => setTickerFocus(false), 180)}
                    autoComplete="off"
                  />
                  {(tickerFocus || tickerHover) && analysisModelSingle !== "soxl_tactical_10m" && (
                    <div
                      className="search-suggestions hoverable-popup"
                      onMouseEnter={() => setTickerHover(true)}
                      onMouseLeave={() => setTickerHover(false)}
                      style={{ zIndex: 10 }}
                    >
                      {filterSuggestions(ticker).length === 0 ? (
                        <div className="search-empty">No results</div>
                      ) : (
                        filterSuggestions(ticker).map((s) => (
                          <div
                            key={s.ticker}
                            className="search-suggestion-row"
                            onMouseDown={() => {
                              setTicker(s.ticker);
                              setTickerFocus(false);
                            }}
                          >
                            <span className="search-suggestion-main">
                              <span className="ticker">{s.ticker}</span>
                              <span className="name">{s.name}</span>
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* FIELD ROW */}
              <div className="field-row">
                {/* Time horizon - hidden for triple_leveraged_bb_lrc_scale */}
                {analysisModelSingle !== "triple_leveraged_bb_lrc_scale" && (
                  <div className="field">
                    <label className="field-label">Time horizon (days)</label>
                    <input
                      className="text-input"
                      type="number"
                      min={1}
                      max={120}
                      value={singleHorizonInput}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow any input during editing (including empty string)
                        setSingleHorizonInput(value);
                        // Try to update the actual state if it's a valid number
                        const numValue = Number(value);
                        if (value !== "" && !isNaN(numValue) && numValue >= 1 && numValue <= 120) {
                          setSingleHorizon(numValue);
                        }
                      }}
                      onBlur={(e) => {
                        // Validate and fix on blur
                        const value = e.target.value;
                        const numValue = Number(value);
                        let finalValue: number;
                        if (value === "" || isNaN(numValue) || numValue < 1) {
                          finalValue = 1;
                        } else if (numValue > 120) {
                          finalValue = 120;
                        } else {
                          finalValue = numValue;
                        }
                        setSingleHorizon(finalValue);
                        setSingleHorizonInput(finalValue.toString());
                      }}
                    />
                  </div>
                )}

                {/* Analysis mode - hidden for triple_leveraged_bb_lrc_scale */}
                {analysisModelSingle !== "triple_leveraged_bb_lrc_scale" && (
                  <div className="field">
                    <label className="field-label">Analysis mode</label>
                    <select
                      className="select-input"
                      value={singleMode}
                      onChange={(e) => setSingleMode(e.target.value as AnalysisMode)}
                    >
                      <option value="standard">Standard</option>
                      <option value="deep">Deep</option>
                      <option value="quick">Quick</option>
                    </select>
                  </div>
                )}

                {/* NEW — Analysis model */}
                <div className="field">
                  <label className="field-label">Analysis model</label>
                  <select
                    className="select-input"
                    value={analysisModelSingle}
                    onChange={(e) => setAnalysisModelSingle(e.target.value)}
                  >
                    <option value="classic">Classic</option>
                    <option value="multi_turn_debate">Multi-Turn Debate</option>
                    <option value="chart_pattern_analysis">Chart Pattern Analysis</option>
                    <option value="soxl_tactical_10m">SOXL Tactical Trend</option>
                    <option value="triple_leveraged_bb_lrc_scale">Triple Leveraged BB LRC Scale</option>
                  </select>
                </div>
              </div>

              {/* Position tracking fields (for SOXL Tactical strategy) */}
              {analysisModelSingle === "soxl_tactical_10m" && (
                <div className="field-row">
                  <div className="field">
                    <label className="field-label">
                      Position Entry Price (optional)
                      <span style={{ fontSize: "0.75rem", color: "#666", marginLeft: "0.25rem" }}>
                        Leave empty if no position
                      </span>
                    </label>
                    <input
                      className="text-input"
                      type="number"
                      step="0.01"
                      min="0"
                      value={positionEntryPrice}
                      onChange={(e) => setPositionEntryPrice(e.target.value)}
                      placeholder="e.g., 35.50"
                    />
                  </div>
                  <div className="field">
                    <label className="field-label">
                      Position Shares (optional)
                      <span style={{ fontSize: "0.75rem", color: "#666", marginLeft: "0.25rem" }}>
                        Leave empty if no position
                      </span>
                    </label>
                    <input
                      className="text-input"
                      type="number"
                      step="0.01"
                      min="0"
                      value={positionShares}
                      onChange={(e) => setPositionShares(e.target.value)}
                      placeholder="e.g., 100"
                    />
                  </div>
                </div>
              )}

              {/* Analysis Model Description Info (shown for Single ETF) */}
              {(() => {
                const modelDesc = getSingleAnalysisModelDescription();
                if (!modelDesc) return null;
                return (
                  <div className="field" style={{ marginBottom: "1rem" }}>
                    <div style={{
                      padding: "1rem",
                      backgroundColor: "#f8f9fa",
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                      fontSize: "0.9rem",
                      lineHeight: "1.6"
                    }}>
                      <strong style={{ display: "block", marginBottom: "0.5rem", color: "#333" }}>
                        {modelDesc.title}:
                      </strong>
                      {modelDesc.content}
                    </div>
                  </div>
                );
              })()}

              {/* FIELD ROW */}
              <div className="field-row">
                {/* Recommendation level */}
                <div className="field">
                  <label className="field-label">Recommendation level</label>
                  <select
                    className="select-input"
                    value={recommendationLevel}
                    onChange={(e) => setRecommendationLevel(e.target.value)}
                  >
                    <option value="Aggressive">Aggressive</option>
                    <option value="Moderate">Moderate</option>
                    <option value="Conservative">Conservative</option>
                  </select>
                </div>

                {/* Language */}
                <div className="field">
                  <label className="field-label">Response Language</label>
                  <select
                    className="select-input"
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                  >
                    <option value="English">English</option>
                    <option value="Korean">Korean (한국어)</option>
                    <option value="Japanese">Japanese (日本語)</option>
                    <option value="Spanish">Spanish (Español)</option>
                    <option value="German">German (Deutsch)</option>
                    <option value="French">French (Français)</option>
                    <option value="Italian">Italian (Italiano)</option>
                  </select>
                </div>
              </div>

              {singleError && <div className="alert alert-error">{singleError}</div>}

              <button
                className="primary-button"
                onClick={handleAnalyzeSingle}
                disabled={singleLoading}
              >
                {singleLoading ? "Analyzing…" : "Analyze ETF"}
              </button>
            </div>
          </section>

          {/* RIGHT PANEL */}
          <section className="panel panel-right">
            <div className="panel-inner">
              {singleData && (
                <div style={{ textAlign: "right", marginBottom: "8px" }}>
                  <button className="primary-button" onClick={handlePrintAnalysis}>
                    Print Analysis
                  </button>
                </div>
              )}

              <div ref={printRef}>
                {!singleData && !singleLoading && !singleError && (
                  <div className="empty-state">
                    <h3>Results will appear here</h3>
                    <p>Run an analysis to view technical, macro, and risk insights.</p>
                  </div>
                )}

                {singleLoading && (
                  <>
                    <AgentProgress analysisModel={analysisModelSingle} isActive={singleLoading} />
                    <LoadingSpinner label="Agents are thinking…" />
                  </>
                )}

                {singleData && (
                  <>
                    <EtfCard data={singleData} />
                    <AnalysisResult 
                      data={singleData} 
                      dialogButton={
                        <button className="primary-button" onClick={handlePrintDialog}>
                          Print Agent Dialog
                        </button>
                      }
                    />
                  </>
                )}
              </div>
            </div>
          </section>
        </main>
      ) : activeTab === "auto_cycle" ? (
        /* ======================= */
        /* AUTO-CYCLE (SOXL/SOXS)  */
        /* ======================= */
        <main style={{ flex: 1, display: "flex", flexDirection: "column", marginTop: "8px" }}>
          <section className="panel" style={{ flex: 1, width: "100%" }}>
            <div className="panel-inner">
              <AutoCycle key="auto-cycle" />
            </div>
          </section>
        </main>
      ) : activeTab === "scalper" ? (
        /* ======================= */
        /* SOXL PULLBACK (MODERATE) */
        /* ======================= */
        <main style={{ flex: 1, display: "flex", flexDirection: "column", marginTop: "8px" }}>
          <section className="panel" style={{ flex: 1, width: "100%" }}>
            <div className="panel-inner">
              <SOXLPullback key="soxl-pullback" enabled={scalperEnabled} onEnabledChange={setScalperEnabled} />
            </div>
          </section>
        </main>
      ) : activeTab === "pair" ? (
        /* ======================= */
        /* PAIR ANALYSIS PANEL     */
        /* ======================= */
        <main className="main-layout">
          <section className="panel panel-left">
            <div className="panel-inner">
              <h2 className="panel-title">Analyze a Pair / Rotation</h2>
              <p className="panel-description">
                Compare two ETFs and determine rotation bias.
              </p>

              {/* PRIMARY TICKER */}
              <div className="field">
                <label className="field-label">Primary ETF</label>
                <div className="ticker-search-wrapper" style={{ position: "relative" }}>
                  <input
                    className="text-input"
                    value={primaryTicker}
                    onChange={(e) => setPrimaryTicker(e.target.value.toUpperCase())}
                    onFocus={() => setPrimaryFocus(true)}
                    onBlur={() => setTimeout(() => setPrimaryFocus(false), 180)}
                    autoComplete="off"
                  />
                  {(primaryFocus || primaryHover) && (
                    <div
                      className="search-suggestions hoverable-popup"
                      onMouseEnter={() => setPrimaryHover(true)}
                      onMouseLeave={() => setPrimaryHover(false)}
                      style={{ zIndex: 10 }}
                    >
                      {filterSuggestions(primaryTicker).length === 0 ? (
                        <div className="search-empty">No results</div>
                      ) : (
                        filterSuggestions(primaryTicker).map((s) => (
                          <div
                            key={s.ticker}
                            className="search-suggestion-row"
                            onMouseDown={() => {
                              setPrimaryTicker(s.ticker);
                              setPrimaryFocus(false);
                            }}
                          >
                            <span className="search-suggestion-main">
                              <span className="ticker">{s.ticker}</span>
                              <span className="name">{s.name}</span>
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* SECONDARY TICKER */}
              <div className="field">
                <label className="field-label">Secondary ETF</label>
                <div className="ticker-search-wrapper" style={{ position: "relative" }}>
                  <input
                    className="text-input"
                    value={secondaryTicker}
                    onChange={(e) => setSecondaryTicker(e.target.value.toUpperCase())}
                    onFocus={() => setSecondaryFocus(true)}
                    onBlur={() => setTimeout(() => setSecondaryFocus(false), 180)}
                    autoComplete="off"
                  />
                  {(secondaryFocus || secondaryHover) && (
                    <div
                      className="search-suggestions hoverable-popup"
                      onMouseEnter={() => setSecondaryHover(true)}
                      onMouseLeave={() => setSecondaryHover(false)}
                      style={{ zIndex: 10 }}
                    >
                      {filterSuggestions(secondaryTicker).length === 0 ? (
                        <div className="search-empty">No results</div>
                      ) : (
                        filterSuggestions(secondaryTicker).map((s) => (
                          <div
                            key={s.ticker}
                            className="search-suggestion-row"
                            onMouseDown={() => {
                              setSecondaryTicker(s.ticker);
                              setSecondaryFocus(false);
                            }}
                          >
                            <span className="search-suggestion-main">
                              <span className="ticker">{s.ticker}</span>
                              <span className="name">{s.name}</span>
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* FIELD ROW */}
              <div className="field-row">
                {/* Time horizon */}
                <div className="field">
                  <label className="field-label">Time horizon (days)</label>
                  <input
                    className="text-input"
                    type="number"
                    min={1}
                    max={120}
                    value={pairHorizon}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty string during editing
                      if (value === "") {
                        setPairHorizon(1); // Set to minimum when empty
                        return;
                      }
                      const numValue = Number(value);
                      // Only update if it's a valid number within range
                      if (!isNaN(numValue) && numValue >= 1 && numValue <= 120) {
                        setPairHorizon(numValue);
                      }
                    }}
                    onBlur={(e) => {
                      // Ensure value is valid on blur
                      const value = e.target.value;
                      const numValue = Number(value);
                      if (value === "" || isNaN(numValue) || numValue < 1) {
                        setPairHorizon(1);
                      } else if (numValue > 120) {
                        setPairHorizon(120);
                      }
                    }}
                  />
                </div>

                {/* Analysis mode */}
                <div className="field">
                  <label className="field-label">Analysis mode</label>
                  <select
                    className="select-input"
                    value={pairMode}
                    onChange={(e) => setPairMode(e.target.value as AnalysisMode)}
                  >
                    <option value="standard">Standard</option>
                    <option value="deep">Deep</option>
                    <option value="quick">Quick</option>
                  </select>
                </div>

                {/* NEW — Analysis Model */}
                <div className="field">
                  <label className="field-label">Analysis model</label>
                  <select
                    className="select-input"
                    value={analysisModelPair}
                    onChange={(e) => setAnalysisModelPair(e.target.value)}
                  >
                    <option value="classic">Classic</option>
                    <option value="multi_turn_debate">Multi-Turn Debate</option>
                  </select>
                </div>
              </div>

              {/* Analysis Model Description Info (shown for Pair & Rotation) */}
              {(() => {
                const modelDesc = getPairAnalysisModelDescription();
                if (!modelDesc) return null;
                return (
                  <div className="field" style={{ marginBottom: "1rem" }}>
                    <div style={{
                      padding: "1rem",
                      backgroundColor: "#f8f9fa",
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                      fontSize: "0.9rem",
                      lineHeight: "1.6"
                    }}>
                      <strong style={{ display: "block", marginBottom: "0.5rem", color: "#333" }}>
                        {modelDesc.title}:
                      </strong>
                      {modelDesc.content}
                    </div>
                  </div>
                );
              })()}

              {pairError && <div className="alert alert-error">{pairError}</div>}

              <button
                className="primary-button"
                onClick={handleAnalyzePair}
                disabled={pairLoading}
              >
                {pairLoading ? "Analyzing…" : "Analyze Pair"}
              </button>
            </div>
          </section>

          {/* RIGHT PANEL */}
          <section className="panel panel-right">
            <div className="panel-inner">
              {pairData && (
                <div style={{ textAlign: "right", marginBottom: "8px" }}>
                  <button className="primary-button" onClick={handlePrintAnalysis}>
                    Print Analysis
                  </button>
                </div>
              )}

              <div ref={pairPrintRef}>
                {!pairData && !pairLoading && !pairError && (
                  <div className="empty-state">
                    <h3>Pair results will appear here</h3>
                    <p>Run a rotation analysis to determine capital flow.</p>
                  </div>
                )}

                {pairLoading && (
                  <>
                    <AgentProgress analysisModel={analysisModelPair} isActive={pairLoading} />
                    <LoadingSpinner label="Agents are comparing…" />
                  </>
                )}

                {pairData && (
                  <>
                    <PairCard data={pairData} />
                    <PairAnalysisResult 
                      data={pairData} 
                      dialogButton={
                        <button className="primary-button" onClick={handlePrintDialog}>
                          Print Agent Dialog
                        </button>
                      }
                    />
                  </>
                )}
              </div>
            </div>
          </section>
        </main>
      ) : activeTab === "screener" ? (
        /* ======================= */
        /* SCREENER PANEL          */
        /* ======================= */
        <main className="main-layout">
          <section className="panel panel-left">
            <div className="panel-inner">
              <h2 className="panel-title">ETF Screener</h2>
              <p className="panel-description">
                Apply screening strategies to analyze ETFs based on specific criteria.
              </p>

              {/* Ticker input */}
              <div className="field">
                <label className="field-label">ETF ticker</label>
                <div className="ticker-search-wrapper" style={{ position: "relative" }}>
                  <input
                    className="text-input"
                    value={screenerTicker}
                    onChange={(e) => setScreenerTicker(e.target.value.toUpperCase())}
                    onFocus={() => setScreenerTickerFocus(true)}
                    onBlur={() => setTimeout(() => setScreenerTickerFocus(false), 180)}
                    autoComplete="off"
                  />
                  {(screenerTickerFocus || screenerTickerHover) && (
                    <div
                      className="search-suggestions hoverable-popup"
                      onMouseEnter={() => setScreenerTickerHover(true)}
                      onMouseLeave={() => setScreenerTickerHover(false)}
                      style={{ zIndex: 10 }}
                    >
                      {filterSuggestions(screenerTicker).length === 0 ? (
                        <div className="search-empty">No results</div>
                      ) : (
                        filterSuggestions(screenerTicker).map((s) => (
                          <div
                            key={s.ticker}
                            className="search-suggestion-row"
                            onMouseDown={() => {
                              setScreenerTicker(s.ticker);
                              setScreenerTickerFocus(false);
                            }}
                          >
                            <span className="search-suggestion-main">
                              <span className="ticker">{s.ticker}</span>
                              <span className="name">{s.name}</span>
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* FIELD ROW */}
              <div className="field-row">
                {/* Screener Strategy */}
                <div className="field">
                  <label className="field-label">Screener</label>
                  {screenerStrategiesLoading ? (
                    <div style={{ color: "#f59e0b", fontSize: "0.875rem", padding: "0.5rem" }}>
                      Loading screener strategies...
                    </div>
                  ) : screenerError ? (
                    <div style={{ fontSize: "0.875rem", padding: "0.5rem" }}>
                      <span style={{ color: "#ef4444" }}>{screenerError}</span>
                      <button
                        type="button"
                        className="primary-button"
                        onClick={() => loadScreenerStrategies()}
                        style={{ marginTop: "0.5rem" }}
                      >
                        Retry
                      </button>
                    </div>
                  ) : screenerStrategies.length === 0 ? (
                    <div style={{ color: "#f59e0b", fontSize: "0.875rem", padding: "0.5rem" }}>
                      No strategies loaded.{" "}
                      <button type="button" className="primary-button" onClick={() => loadScreenerStrategies()} style={{ marginLeft: "0.5rem" }}>
                        Retry
                      </button>
                    </div>
                  ) : (
                    <select
                      className="select-input"
                      value={screenerStrategy || ""}
                      onChange={(e) => setScreenerStrategy(e.target.value)}
                    >
                      {screenerStrategies.map((strategy) => (
                        <option key={strategy.id} value={strategy.id}>
                          {strategy.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Chart Interval */}
                <div className="field">
                  <label className="field-label">
                    Chart Interval
                    {(() => {
                      const strategy = screenerStrategies.find(s => s.id === screenerStrategy);
                      const requiredInterval = strategy?.required_chart_interval;
                      return requiredInterval ? (
                        <span style={{ fontSize: "0.75rem", color: "#666", marginLeft: "8px", fontWeight: "normal" }}>
                          (Required: {requiredInterval})
                        </span>
                      ) : null;
                    })()}
                  </label>
                  <select
                    className="select-input"
                    value={chartInterval}
                    onChange={(e) => setChartInterval(e.target.value as ChartInterval)}
                    disabled={(() => {
                      const strategy = screenerStrategies.find(s => s.id === screenerStrategy);
                      return !!strategy?.required_chart_interval;
                    })()}
                    title={(() => {
                      const strategy = screenerStrategies.find(s => s.id === screenerStrategy);
                      const requiredInterval = strategy?.required_chart_interval;
                      return requiredInterval 
                        ? `${strategy?.name} requires ${requiredInterval} intervals` 
                        : "";
                    })()}
                  >
                    <option value="1m">1 Minute</option>
                    <option value="3m">3 Minutes</option>
                    <option value="5m">5 Minutes</option>
                    <option value="15m">15 Minutes</option>
                    <option value="30m">30 Minutes</option>
                    <option value="45m">45 Minutes</option>
                    <option value="1h">1 Hour</option>
                    <option value="2h">2 Hours</option>
                    <option value="3h">3 Hours</option>
                    <option value="4h">4 Hours</option>
                    <option value="6h">6 Hours</option>
                    <option value="8h">8 Hours</option>
                    <option value="12h">12 Hours</option>
                    <option value="1D">1 Day</option>
                    <option value="3D">3 Days</option>
                    <option value="1W">1 Week</option>
                    <option value="1M">1 Month</option>
                    <option value="3M">3 Months</option>
                    <option value="6M">6 Months</option>
                    <option value="12M">12 Months</option>
                  </select>
                </div>

                {/* Recommendation level */}
                <div className="field">
                  <label className="field-label">Recommendation level</label>
                  <select
                    className="select-input"
                    value={recommendationLevel}
                    onChange={(e) => setRecommendationLevel(e.target.value)}
                  >
                    <option value="Aggressive">Aggressive</option>
                    <option value="Moderate">Moderate</option>
                    <option value="Conservative">Conservative</option>
                  </select>
                </div>

                {/* Language */}
                <div className="field">
                  <label className="field-label">Response Language</label>
                  <select
                    className="select-input"
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                  >
                    <option value="English">English</option>
                    <option value="Korean">Korean (한국어)</option>
                    <option value="Japanese">Japanese (日本語)</option>
                    <option value="Spanish">Spanish (Español)</option>
                    <option value="German">German (Deutsch)</option>
                    <option value="French">French (Français)</option>
                    <option value="Italian">Italian (Italiano)</option>
                  </select>
                </div>
              </div>

              {/* Strategy Description Info (shown for all strategies) */}
              {currentStrategy && (() => {
                const strategyDesc = getStrategyDescription();
                if (!strategyDesc) return null;
                return (
                  <div className="field" style={{ marginBottom: "1rem" }}>
                    <div style={{
                      padding: "1rem",
                      backgroundColor: "#f8f9fa",
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                      fontSize: "0.9rem",
                      lineHeight: "1.6"
                    }}>
                      <strong style={{ display: "block", marginBottom: "0.5rem", color: "#333" }}>
                        {strategyDesc.title}:
                      </strong>
                      {strategyDesc.content}
                    </div>
                  </div>
                );
              })()}

              {/* User Prompt (shown when User Specified is selected) */}
              {showUserPrompt && (
                <div className="field">
                  <label className="field-label">Custom Screener Prompt</label>
                  <textarea
                    className="text-input"
                    rows={4}
                    value={userPrompt}
                    onChange={(e) => setUserPrompt(e.target.value)}
                    placeholder="Enter your custom analysis prompt here..."
                    style={{
                      resize: "vertical",
                      fontFamily: "inherit",
                      fontSize: "0.9rem",
                    }}
                  />
                </div>
              )}

              {(screenerError || form4Error) && (
                <div className="alert alert-error">{screenerError || form4Error}</div>
              )}

              <button
                className="primary-button"
                onClick={handleAnalyzeScreener}
                disabled={screenerLoading || form4Loading}
              >
                {screenerStrategy === "form4_insiders"
                  ? (form4Loading ? "Running Form 4…" : "Run Screener")
                  : (screenerLoading ? "Screening…" : "Analyze Screener")}
              </button>
            </div>
          </section>

          {/* RIGHT PANEL */}
          <section className="panel panel-right">
            <div className="panel-inner">
              {(screenerData && screenerStrategy !== "form4_insiders") && (
                <div style={{ textAlign: "right", marginBottom: "8px" }}>
                  <button className="primary-button" onClick={handlePrintAnalysis}>
                    Print Analysis
                  </button>
                </div>
              )}
              {form4Data && (
                <div style={{ textAlign: "right", marginBottom: "8px" }}>
                  <button className="primary-button" onClick={handlePrintAnalysis}>
                    Print
                  </button>
                </div>
              )}

              <div ref={screenerPrintRef}>
                {screenerStrategy === "form4_insiders" ? (
                  <>
                    {!form4Data && !form4Loading && !form4Error && (
                      <div className="empty-state">
                        <h3>Insider Form 4 results will appear here</h3>
                        <p>Click &quot;Run Screener&quot; to load tickers with recent Form 4 filings (SEC EDGAR + Polygon price).</p>
                      </div>
                    )}
                    {form4Loading && (
                      <>
                        <LoadingSpinner label="Loading Form 4 filings…" />
                      </>
                    )}
                    {form4Data && <Form4ScreenerResult data={form4Data} />}
                  </>
                ) : (
                  <>
                    {!screenerData && !screenerLoading && !screenerError && (
                      <div className="empty-state">
                        <h3>Screener results will appear here</h3>
                        <p>Select a screener strategy and run analysis to view results.</p>
                      </div>
                    )}
                    {screenerLoading && (
                      <>
                        <AgentProgress analysisModel="classic" isActive={screenerLoading} />
                        <LoadingSpinner label="Screener agents analyzing…" />
                      </>
                    )}
                    {screenerData && (
                      <>
                        <ScreenerCard data={screenerData} />
                        <ScreenerResult data={screenerData} />
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </section>
        </main>
      ) : activeTab === "performance" ? (
        /* ======================= */
        /* PERFORMANCE DASHBOARD    */
        /* ======================= */
        <main className="main-layout">
          <section className="panel panel-left">
            <div className="panel-inner">
              <h2 className="panel-title">Performance Dashboard</h2>
              <p className="panel-description">
                View performance statistics for tracked recommendations and strategies.
              </p>
              <PerformanceDashboard />
            </div>
          </section>
          <section className="panel panel-right">
            <div className="panel-inner">
              <PerformanceDashboardDetails />
            </div>
          </section>
        </main>
      ) : activeTab === "positions" ? (
        /* ======================= */
        /* POSITION TRACKER        */
        /* ======================= */
        <main className="main-layout">
          <section className="panel panel-left" style={{ width: "100%" }}>
            <div className="panel-inner">
              <h2 className="panel-title">Position Tracker</h2>
              <p className="panel-description">
                Track open positions and receive exit signal recommendations.
              </p>
              <PositionTracker />
            </div>
          </section>
        </main>
      ) : null}

      <footer className="app-footer">
        <span>ETF Brain • Local prototype</span>
        <span className="footer-right">
          Backend: <code>{import.meta.env.VITE_API_URL}</code>
        </span>
      </footer>
    </div>
  );
}

export default App;














